/**
 * Atlas PM · 用户管理视图（P4.3）
 *
 * 列表列：用户名 / 姓名 / 组织 / 角色 / 数据范围 / 状态 / 最后登录 + 操作
 * 能力：分页（后端）、关键字搜索、新建、编辑、启用/停用、重置口令
 *
 * ⭐ 前端拦截：不允许停用自己（当前登录账号那一行，停用按钮置灰）。
 *    后端另有硬拦截（USER_CANNOT_MODIFY_SELF），前端置灰只是体验。
 */

import { useEffect, useMemo, useState } from 'react';
import Panel from '../../../components/Panel';
import Badge from '../../../components/Badge';
import Button from '../../../components/Button';
import { useAuthStore } from '../../../stores/auth';
import { scopeLabel, type UserItem } from './api';
import { useOrgTree, useRoleList, useUserList } from './useUsers';
import {
  ResetPasswordDialog,
  StatusConfirmDialog,
  UserFormDialog,
} from './UserDialogs';
import './users.css';

const PAGE_SIZE = 10;

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(
    d.getMinutes(),
  )}`;
}

export default function UsersView() {
  const me = useAuthStore((s) => s.user);

  const [page, setPage] = useState(1);
  const [keywordInput, setKeywordInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | '1' | '0'>('all');

  const [formDialog, setFormDialog] = useState<{ mode: 'create' | 'edit'; user: UserItem | null } | null>(
    null,
  );
  const [statusUser, setStatusUser] = useState<UserItem | null>(null);
  const [resetUser, setResetUser] = useState<UserItem | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // 搜索防抖：输入停止 300ms 后生效，并回到第一页
  useEffect(() => {
    const t = setTimeout(() => {
      setKeyword(keywordInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [keywordInput]);

  // 成功提示自动消失
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 3600);
    return () => clearTimeout(t);
  }, [notice]);

  const query = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      keyword: keyword || undefined,
      ...(statusFilter !== 'all' ? { status: Number(statusFilter) } : {}),
    }),
    [page, keyword, statusFilter],
  );
  const { data, isLoading, isError, error, isFetching } = useUserList(query);
  const orgsQuery = useOrgTree();
  const rolesQuery = useRoleList();

  const orgs = orgsQuery.data ?? [];
  const roles = rolesQuery.data?.items ?? [];

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const isSelf = (u: UserItem) =>
    (me?.userId != null && u.id === me.userId) || (me?.username != null && u.username === me.username);

  /**
   * ⭐ P6.39：该用户的「组织」列是否有意义。
   *
   * 三类角色的可见范围来源：
   *   超级管理员 → 天生全部（与组织无关）
   *   省管理员   → 由所属组织（省份）推导，组织 = 范围来源 ✅ 需显示
   *   操作员     → 由「本人参与的项目」推导，与组织无关 ❌ 不显示
   *
   * `app_user.org_id` 是 NOT NULL 外键，操作员/超管建号时会自动挂到建号者组织下，
   * 那只是【归属标记】。若在此列照实渲染「总部」，管理员会误以为操作员有组织范围。
   */
  const showsOrg = (u: UserItem): boolean => {
    if (u.isSuperAdmin) return false;
    const codes = u.roles.map((r) => r.code);
    // 仅当用户【只有】省管理员这一类带组织语义的角色时才显示
    return codes.includes('PROVINCE_ADMIN');
  };

  if (isError) {
    return (
      <Panel title="用户管理" extra={<Badge tone="alert">加载失败</Badge>}>
        <div className="p43-error">
          ⚠ {error instanceof Error ? error.message : '用户列表加载失败'}
        </div>
      </Panel>
    );
  }

  return (
    <Panel
      title="用户管理"
      extra={
        <span className="p43-count">
          {isFetching && <span className="p43-count__dot" aria-hidden />}
          共 <b>{total}</b> 个账号
        </span>
      }
      flush
    >
      <div className="p43-wrap">
        <div className="p43-toolbar">
          <div className="p43-search">
            <span className="p43-search__icon">⌕</span>
            <input
              className="p43-search__input"
              value={keywordInput}
              placeholder="搜索用户名 / 姓名"
              onChange={(e) => setKeywordInput(e.target.value)}
            />
            {keywordInput && (
              <button
                type="button"
                className="p43-search__clear"
                aria-label="清空搜索"
                onClick={() => setKeywordInput('')}
              >
                ✕
              </button>
            )}
          </div>
          <label className="p43-statusfilter">
            <select
              data-testid="user-filter-status"
              aria-label="按状态筛选用户"
              className="p43-statusfilter__select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as 'all' | '1' | '0');
                setPage(1);
              }}
            >
              <option value="all">全部状态</option>
              <option value="1">仅启用</option>
              <option value="0">仅停用</option>
            </select>
          </label>
          <Button
            variant="primary"
            onClick={() => setFormDialog({ mode: 'create', user: null })}
          >
            ＋ 新建用户
          </Button>
        </div>

        {notice && (
          <div className="p43-notice" role="status">
            <span className="p43-notice__icon">✓</span>
            {notice}
          </div>
        )}

        <div className="p43-table-wrap">
          <table className="p43-table">
            <thead>
              <tr>
                <th>用户名</th>
                <th>姓名</th>
                <th>组织</th>
                <th>角色</th>
                <th>数据范围</th>
                <th>状态</th>
                <th>最后登录</th>
                <th className="p43-th--actions">操作</th>
              </tr>
            </thead>
            <tbody className="p43-tbody">
              {isLoading && items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p43-td--center p43-loading">
                    加载中…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p43-td--center p43-empty">
                    {keyword ? `没有匹配「${keyword}」的用户` : '暂无用户'}
                  </td>
                </tr>
              ) : (
                items.map((u) => {
                  const self = isSelf(u);
                  const enabled = u.status === 1;
                  return (
                    <tr key={u.id} data-username={u.username} data-self={self ? '1' : '0'}>
                      <td className="p43-cell--user">
                        <span className="p43-mono">{u.username}</span>
                        {u.isSuperAdmin && <span className="p43-super">超管</span>}
                      </td>
                      <td className="p43-cell--name">{u.displayName}</td>
                      {/*
                        ⭐ P6.39：操作员与超管「没有组织概念」——
                        其可见范围由角色决定（操作员=仅参与的项目；超管=全部），
                        orgId 只是 NOT NULL 外键的归属标记，不代表可见范围。
                        直接显示组织名（如「总部」）会让管理员误以为操作员有组织范围，
                        故这两类角色显示「—」；仅省管理员的组织才是真实的范围来源。
                      */}
                      {/*
                        ⭐ P7.4：省管理员可多组织。列表只有主归属组织名（org_id 对应），
                        多选时补「等 N 个」，长文本由整表 nowrap + 横向滚动承接。
                      */}
                      <td>
                        {showsOrg(u)
                          ? `${u.orgName ?? '—'}${
                              u.scopeOrgIds.length > 1 ? ` 等 ${u.scopeOrgIds.length} 个` : ''
                            }`
                          : '—'}
                      </td>
                      <td>
                        <div className="p43-roles-cell">
                          {u.roles.length === 0
                            ? '—'
                            : u.roles.map((r) => (
                                <span key={r.roleId} className="p43-role-pill" title={r.code}>
                                  {r.name}
                                </span>
                              ))}
                        </div>
                      </td>
                      <td>
                        {scopeLabel(u.scopeType)}
                        {u.scopeType !== 0 && u.scopeOrgIds.length > 0 && (
                          <span className="p43-scope-n"> · {u.scopeOrgIds.length} 个组织</span>
                        )}
                      </td>
                      <td>
                        <span className="p43-status" data-status={u.status}>
                          <Badge tone={enabled ? 'ok' : 'idle'}>{enabled ? '启用' : '停用'}</Badge>
                        </span>
                      </td>
                      <td className="p43-cell--time">{fmtDateTime(u.lastLoginAt)}</td>
                      <td>
                        <div className="p43-actions">
                          <button
                            type="button"
                            className="p43-act"
                            data-act="edit"
                            onClick={() => setFormDialog({ mode: 'edit', user: u })}
                          >
                            编辑
                          </button>
                          <button
                            type="button"
                            className="p43-act"
                            data-act="status"
                            data-target-status={enabled ? 0 : 1}
                            disabled={self}
                            title={self ? '不能停用自己的账号' : enabled ? '停用该用户' : '启用该用户'}
                            onClick={() => setStatusUser(u)}
                          >
                            {enabled ? '停用' : '启用'}
                          </button>
                          <button
                            type="button"
                            className="p43-act"
                            data-act="reset"
                            onClick={() => setResetUser(u)}
                          >
                            重置密码
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="p43-pager">
          <span className="p43-pager__info">
            第 {page} / {totalPages} 页 · 共 {total} 条
          </span>
          <div className="p43-pager__btns">
            <Button
              size="sm"
              variant="secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              上一页
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              下一页
            </Button>
          </div>
        </div>
      </div>

      <UserFormDialog
        open={formDialog !== null}
        mode={formDialog?.mode ?? 'create'}
        user={formDialog?.user ?? null}
        orgs={orgs}
        roles={roles}
        onClose={() => setFormDialog(null)}
        onSaved={(msg) => setNotice(msg)}
      />

      <StatusConfirmDialog
        open={statusUser !== null}
        user={statusUser}
        onClose={() => setStatusUser(null)}
        onDone={(msg) => setNotice(msg)}
      />

      <ResetPasswordDialog
        open={resetUser !== null}
        user={resetUser}
        onClose={() => setResetUser(null)}
        onDone={(msg) => setNotice(msg)}
      />
    </Panel>
  );
}
